/**
 * La lista de la compra de una semana, derivada del plan.
 *
 * No se guarda ninguna lista: se calcula cada vez a partir de las recetas
 * planificadas más los añadidos a mano. Lo único que se guarda son los hechos:
 * qué se añadió suelto y qué se ha ido tachando.
 */
import {
  itemKeyOf,
  normaliseName,
  payloadOf,
  sectionOf,
  SHOPPING_SECTIONS,
  type HouseholdEvent,
  type ShoppingSection,
  type Unit,
} from './events.js'
import { planBySlot, recipesById } from './plate.js'
import { weekDays, type WeekKey } from './time.js'

export interface ShoppingLine {
  itemKey: string
  /** Cómo se escribe, con la grafía de la primera vez que apareció. */
  name: string
  quantity: number | null
  unit: Unit | null
  section: ShoppingSection
  bought: boolean
  /** Recetas de las que sale, para saber por qué está en la lista. */
  recipes: string[]
  /** Añadido a mano, sin receta detrás. */
  manual: boolean
  /**
   * Algo de lo que suma esta línea venía sin cantidad, así que el número es
   * un mínimo y no el total. Preferimos decirlo a fingir una suma exacta.
   */
  hasUnknownQuantity: boolean
  /**
   * El mismo alimento aparece en otra línea con otra unidad (200 g y 2 ud).
   * No se convierten a ciegas: se muestran juntas y ya decide quien compra.
   */
  splitByUnit: boolean
}

export interface ShoppingGroup {
  section: ShoppingSection
  lines: ShoppingLine[]
}

export interface ShoppingList {
  weekKey: WeekKey
  groups: ShoppingGroup[]
  total: number
  bought: number
}

interface Accumulator {
  itemKey: string
  name: string
  quantity: number | null
  unit: Unit | null
  section: ShoppingSection
  recipes: Set<string>
  manual: boolean
  hasUnknownQuantity: boolean
}

/** Lo tachado de esa semana: por línea, manda el registro más reciente. */
export function boughtKeys(weekKey: WeekKey, events: readonly HouseholdEvent[]): Set<string> {
  const latest = new Map<string, { at: string; bought: boolean }>()
  for (const event of events) {
    if (event.deletedAt || event.type !== 'shopping_check') continue
    const payload = payloadOf(event, 'shopping_check')
    if (!payload || payload.weekKey !== weekKey) continue
    const current = latest.get(payload.itemKey)
    if (!current || event.updatedAt > current.at) {
      latest.set(payload.itemKey, { at: event.updatedAt, bought: payload.bought })
    }
  }
  return new Set([...latest].filter(([, value]) => value.bought).map(([key]) => key))
}

export function shoppingList(weekKey: WeekKey, events: readonly HouseholdEvent[]): ShoppingList {
  const recipes = recipesById(events)
  const plan = planBySlot(events)
  const days = new Set(weekDays(weekKey))
  const lines = new Map<string, Accumulator>()

  for (const [, { payload: entry }] of plan) {
    if (!days.has(entry.dayKey) || !entry.recipeId) continue
    const recipe = recipes.get(entry.recipeId)
    if (!recipe) continue
    for (const ingredient of recipe.ingredients) {
      const key = itemKeyOf(ingredient.name, ingredient.unit)
      add(lines, key, {
        name: ingredient.name,
        quantity: ingredient.quantity,
        unit: ingredient.unit,
        section: sectionOf(ingredient),
        recipe: recipe.name,
        manual: false,
      })
    }
  }

  for (const event of events) {
    if (event.deletedAt || event.type !== 'shopping_extra') continue
    const extra = payloadOf(event, 'shopping_extra')
    if (!extra || extra.weekKey !== weekKey) continue
    add(lines, itemKeyOf(extra.name, extra.unit), {
      name: extra.name,
      quantity: extra.quantity,
      unit: extra.unit,
      section: extra.section,
      recipe: null,
      manual: true,
    })
  }

  const unitsByName = new Map<string, Set<string>>()
  for (const line of lines.values()) {
    const name = normaliseName(line.name)
    const units = unitsByName.get(name) ?? new Set<string>()
    units.add(line.unit ?? '')
    unitsByName.set(name, units)
  }

  const bought = boughtKeys(weekKey, events)
  const resolved: ShoppingLine[] = [...lines.values()].map((line) => ({
    itemKey: line.itemKey,
    name: line.name,
    quantity: line.quantity,
    unit: line.unit,
    section: line.section,
    bought: bought.has(line.itemKey),
    recipes: [...line.recipes].sort((a, b) => a.localeCompare(b, 'es')),
    manual: line.manual,
    hasUnknownQuantity: line.hasUnknownQuantity,
    splitByUnit: (unitsByName.get(normaliseName(line.name))?.size ?? 1) > 1,
  }))

  const groups: ShoppingGroup[] = SHOPPING_SECTIONS.map((section) => ({
    section,
    lines: resolved
      .filter((line) => line.section === section)
      .sort((a, b) => a.name.localeCompare(b.name, 'es')),
  })).filter((group) => group.lines.length > 0)

  return {
    weekKey,
    groups,
    total: resolved.length,
    bought: resolved.filter((line) => line.bought).length,
  }
}

function add(
  lines: Map<string, Accumulator>,
  key: string,
  input: {
    name: string
    quantity: number | null
    unit: Unit | null
    section: ShoppingSection
    recipe: string | null
    manual: boolean
  },
): void {
  const existing = lines.get(key)
  if (!existing) {
    lines.set(key, {
      itemKey: key,
      name: input.name,
      quantity: input.quantity,
      unit: input.unit,
      section: input.section,
      recipes: new Set(input.recipe ? [input.recipe] : []),
      manual: input.manual,
      hasUnknownQuantity: input.quantity === null,
    })
    return
  }

  if (input.quantity === null) existing.hasUnknownQuantity = true
  else existing.quantity = (existing.quantity ?? 0) + input.quantity
  if (input.recipe) existing.recipes.add(input.recipe)
  if (input.manual) existing.manual = true
}
